import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  ReactNode
} from 'react';
import {
  Auth0Client,
  RedirectLoginOptions,
  LogoutOptions,
  GetTokenSilentlyOptions,
  User
} from '@auth0/auth0-spa-js';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router';
import { Auth0StateInformation } from 'helpers/orderDataHelper/types';
import config from '../../Config';
import {
  SOLO_PRODUCT_IDS, STEP_PATH
} from '../../constants';

export type Auth0Props = {
  children: ReactNode;
  audience?: string;
  redirectUri?: string;
}

export type UserType = {
  email: string;
  [key: string]: string;
};

export type AppStateType = {
  clientInformation: Auth0StateInformation
  productId: string;
  promoCode: string;
}

type Auth0ContextType = {
  user?: User;
  isAuthenticated?: boolean;
  isLoading: boolean;
  error?: Error;
  appState?: AppStateType;
  logout: (options?: LogoutOptions) => void;
  loginWithRedirect: (options?: RedirectLoginOptions) => void;
  loginAfterLogout: (options?: RedirectLoginOptions) => void;
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>;
}

export const Auth0Context = createContext<Auth0ContextType>({
  isLoading: true,
  logout: () => '',
  loginWithRedirect: () => '',
  loginAfterLogout: () => '',
  getTokenSilently: () => Promise.resolve('')
});

export const useAuth0 = (): Auth0ContextType => useContext(Auth0Context);

const CODE_RE = /[?&]code=[^&]+/;
const STATE_RE = /[?&]state=[^&]+/;
const ERROR_RE = /[?&]error=[^&]+/;

const hasAuthParams = (searchParams: string): boolean =>
  (CODE_RE.test(searchParams) && STATE_RE.test(searchParams)) ||
  ERROR_RE.test(searchParams);

const getPath = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  params.delete('state');
  params.delete('code');
  params.delete('use-legacy');
  const query = new URLSearchParams(params).toString();
  return pathname + (query ? `?${query}` : '');
};

const Auth0: React.FC<Auth0Props> = ({
  children,
  audience,
  redirectUri
}) => {
  // have to parse url since SessionProvider is child of this Auth0
  // and we need Auth0 in SessionProvider so cannot swap the hierarchy
  const history = useHistory();
  const location = useLocation();
  const { search, pathname } = location;
  const { productId } = qs.parse(search, { ignoreQueryPrefix: true });
  const isSolo = SOLO_PRODUCT_IDS.includes(productId as string);
  const isQuote = pathname.includes(STEP_PATH.QUOTE.ROOT);
  const clientId = isQuote ? config.CHECKOUT_AUTH0_CLIENT_ID_DISABLED_EMAIL : config.CHECKOUT_AUTH0_CLIENT_ID;
  const [client] = useState(new Auth0Client({
    domain: config.MYOB_ID_DOMAIN,
    clientId: clientId,
    authorizationParams: {
      audience,
      redirect_uri: redirectUri,
      ...(isSolo && { organization: config.CHECKOUT_AUTH0_CLIENT_SOLO_ORGANIZATION })
    }
  }));

  const [user, setUser] = useState<User>();
  const [appState, setAppState] = useState<AppStateType | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    if (!hasAuthParams(search)) {
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        const result = await client.handleRedirectCallback();
        history.replace(getPath(pathname, search));
        setAppState(result.appState);
        const userInfo = await client.getUser();
        setUser(userInfo);
        setIsAuthenticated(!!userInfo);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
      }
    })();
  }, [client]);

  const logout = useCallback((options?: LogoutOptions) => client.logout(options), [client]);

  const getTokenSilently = useCallback(
    (options?: GetTokenSilentlyOptions) => client.getTokenSilently(options),
    [client]
  );

  const loginWithRedirect = useCallback(
    (options?: RedirectLoginOptions) => client.loginWithRedirect(options),
    [client]
  );

  const loginAfterLogout = useCallback(
    (options?: RedirectLoginOptions): Promise<void> =>
      client.loginWithRedirect({
        ...options,
        openUrl: (url) => client.logout({ logoutParams: { returnTo: url } })
      }).catch((err) => setError(err)),
    [client]
  );

  return (
    <Auth0Context.Provider value={{
      user,
      appState,
      isAuthenticated,
      isLoading,
      error,
      logout,
      loginWithRedirect,
      loginAfterLogout,
      getTokenSilently
    }}
    >
      {children}
    </Auth0Context.Provider>
  );
};

export default Auth0;

